/**
 * Labstep
 *
 * @module components/EntityImport/Action/Create
 * @desc A massive wizard modal to create an EntityImport
 */

import { EntityImportActionCreateStepsFieldDefinitionsContainer as EntityImportActionCreateStepsFieldDefinitions } from 'labstep-web/components/EntityImport/Action/Create/Steps/FieldDefinitions';
import { EntityImportActionCreateStepsImportFileMapping } from 'labstep-web/components/EntityImport/Action/Create/Steps/ImportFileMapping';
import { EntityImportActionCreateStepsImportMode } from 'labstep-web/components/EntityImport/Action/Create/Steps/ImportMode';
import { EntityImportActionCreateStepsPreviewContainer } from 'labstep-web/components/EntityImport/Action/Create/Steps/Preview';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import { ModalWizard } from 'labstep-web/core/Modal/Wizard';
import { GridEntityImportService } from 'labstep-web/grid/EntityImport/services/grid-entity-import.service';
import { GridEntityImportExcelService } from 'labstep-web/grid/EntityImport/services/grid-entity-import-excel.service';
import { Device } from 'labstep-web/models/device.model';
import {
  EntityImport,
  type EntityImportColDef,
  type EntityImportDataRowData,
} from 'labstep-web/models/entity-import.model';
import type { Resource } from 'labstep-web/models/resource.model';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import type {
  EntityImportActionCreateContainerProps,
  EntityImportActionCreateProps,
} from './types';

export const ENTITY_IMPORT_CREATE_ROUTE_ID = 'entity-import-create';
export const N_EMPTY_ROWS = 50;

enum Steps {
  ImportMode = 'Import Mode',
  ImportFileMapping = 'Import File Mapping',
  FieldDefinitions = 'Field Definitions',
  Preview = 'Preview & Validate',
}

export const EntityImportActionCreate: React.FC<
  EntityImportActionCreateProps
> = ({
  entityName,
  templateId,
  importData,
  setImportData,
  create,
}) => {
  const [importEntityName, setImportEntityName] =
    useState<typeof entityName>(entityName);
  const [headerRowIndex, setHeaderRowIndex] = useState<number>(0);
  const [nameColumn, setNameColumn] = useState<string | null>(null);
  const [rowData, setRowData] = useState<EntityImportDataRowData>([]);
  const [columnDefs, setColumnDefs] = useState<EntityImportColDef[]>(
    [],
  );
  const [selectedTemplateEntity, setSelectedTemplateEntity] =
    useState<Resource | Device | null>(null);

  const reset = useCallback((): void => {
    setImportData(undefined);
    setImportEntityName(entityName);
    setHeaderRowIndex(0);
    setNameColumn(null);
    setRowData([]);
    setColumnDefs([]);
    setSelectedTemplateEntity(null);
  }, []);

  const steps = useMemo(() => {
    const stepsAll = [
      Steps.ImportMode,
      Steps.ImportFileMapping,
      Steps.FieldDefinitions,
      Steps.Preview,
    ];
    if (!importData) {
      stepsAll.splice(1, 1);
    }
    if (entityName === Device.entityName) {
      stepsAll.splice(0, 1);
    }
    return stepsAll;
  }, [importData, entityName]);

  return (
    <ModalWizard
      mode="fullscreen"
      routeId={ENTITY_IMPORT_CREATE_ROUTE_ID}
      title="Import"
      onClose={() => reset()}
      steps={steps}
    >
      {entityName !== Device.entityName && (
        <EntityImportActionCreateStepsImportMode
          index={steps.indexOf(Steps.ImportMode)}
          entityName={entityName}
          onContinue={(selectedEntityName): void => {
            setImportEntityName(selectedEntityName);
          }}
        />
      )}
      {importData && (
        <EntityImportActionCreateStepsImportFileMapping
          index={steps.indexOf(Steps.ImportFileMapping)}
          importData={importData}
          entityName={entityName}
          onContinue={(selectedHeaderRow): void => {
            setHeaderRowIndex(selectedHeaderRow);
          }}
        />
      )}
      <EntityImportActionCreateStepsFieldDefinitions
        templateId={templateId}
        index={steps.indexOf(Steps.FieldDefinitions)}
        entityName={importEntityName}
        nameColumn={nameColumn}
        onContinue={(template, headerMap) => {
          const newColumnDefs =
            GridEntityImportService.getColumnDefsTemplate(
              importEntityName,
              template,
            );
          setSelectedTemplateEntity(template);
          if (importData) {
            const newRowData =
              GridEntityImportExcelService.generateRowData(
                headerMap,
                newColumnDefs,
                importData,
                headerRowIndex,
              );
            setRowData(newRowData);
          } else {
            setRowData(
              GridEntityImportService.generateEmptyRowData(
                N_EMPTY_ROWS,
                newColumnDefs,
              ),
            );
          }
          setColumnDefs(newColumnDefs);
        }}
        importData={importData}
        headerRowIndex={headerRowIndex}
      />
      {selectedTemplateEntity && (
        <EntityImportActionCreateStepsPreviewContainer
          index={steps.indexOf(Steps.Preview)}
          rowData={rowData}
          columnDefs={columnDefs}
          entityName={importEntityName}
          onContinue={(): void => reset()}
          template={selectedTemplateEntity}
          create={create}
        />
      )}
    </ModalWizard>
  );
};

export const EntityImportActionCreateContainer: React.FC<
  EntityImportActionCreateContainerProps
> = (props) => (
  <EntityCreateContainer entityName={EntityImport.entityClass}>
    {({ create, status }): React.ReactElement => (
      <EntityImportActionCreate
        create={create}
        status={status}
        {...props}
      />
    )}
  </EntityCreateContainer>
);
